import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { selectDocumentTypes } from '../../../store/dataSelectors';
import {
    loadAppointmentTypeDetailsByContractIds,
    loadDocumentTypes
} from '../../../store/directusService';
import {
    getPreSelects,
    updatePreSelects,
    updateSelectedItems
} from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { DOCUMENT_TYPES_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import IconError from '../../IconError';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateDocumentsAdmin';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const DocumentsAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const viewRoles = [LOCAL_ADMIN, SUPERUSER];
    const acceptedRoles = [SUPERUSER];
    const collectionName = 'document types';
    const urlName = 'document_type';
    const calendarUrlName = 'calendar_appointment_type';
    const submitMessage = 'Updating document types collection';

    const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState([]);
    const [preSelectedDocumentTypes, setPreSelectedDocumentTypes] = useState([]);
    const [preSelectedDocumentTypeIds, setPreSelectedDocumentTypeIds] = useState([]);
    const [essentialDocumentTypes, setEssentialDocumentTypes] = useState([]);
    const [keys, setKeys] = useState({ documentTypes: '1' });
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const documentTypes = useSelector(selectDocumentTypes);
    const appointmentTypeDetailsForContracts = useSelector(
        (state) => state.entities.directusService.appointmentTypeDetailsForContracts
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) contractSetup();
    }, [contractId]);

    useEffect(() => {
        if (documentTypes?.length > 0) {
            setEssentialDocumentTypes([]);
            const { preSelects, preSelectIds } = updatePreSelects(
                urlName,
                documentTypes,
                contractId
            );
            setPreSelectedDocumentTypes(preSelects);
            setPreSelectedDocumentTypeIds(preSelectIds);
        }
    }, [documentTypes]);

    useEffect(() => {
        if (appointmentTypeDetailsForContracts?.length < 1 || documentTypes?.length < 1) return;

        const preSelects = getPreSelects(
            calendarUrlName,
            appointmentTypeDetailsForContracts,
            contractId
        );
        const documentTypeMatches = preSelects
            .map((el) =>
                documentTypes.find((entry) => entry.name.toLowerCase() === el.name.toLowerCase())
            )
            .filter((el) => el);
        setEssentialDocumentTypes(documentTypeMatches);
    }, [appointmentTypeDetailsForContracts, documentTypes]);

    useEffect(() => {
        setIsDisabled(
            Object.values(errors).some((el) => el.error) || errors?.appointmentTypes?.length > 0
        );
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(submitMessage) || errorMessage) {
            contractSetup();
            setIsDisabled(false);
        }
    }, [successMessage, errorMessage]);

    //  HELPER FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (documentTypes?.length < 1) return DOCUMENT_TYPES_ERROR;
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const contractSetup = () => {
        setSelectedDocumentTypeIds([]);
        setPreSelectedDocumentTypes([]);
        setPreSelectedDocumentTypeIds([]);
        setKeys(clearKeys(keys));
        setEssentialDocumentTypes([]);
        setErrors({});
        dispatch(loadDocumentTypes());
        dispatch(loadAppointmentTypeDetailsByContractIds([contractId]));
    };

    // EVENT HANDLERS
    const onDocumentTypesChange = (chosenIds) => {
        setErrors({});
        const newChosenIds = chosenIds.filter((el) => el);
        if (!newChosenIds) chosenIds = [];
        setSelectedDocumentTypeIds(chosenIds);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsDisabled(true);
        const newErrors = validate(
            essentialDocumentTypes,
            selectedDocumentTypeIds,
            preSelectedDocumentTypeIds
        );

        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }

        updateSelectedItems(
            urlName,
            documentTypes,
            selectedDocumentTypeIds,
            contractId,
            collectionName,
            dispatch
        );
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            onSubmit={onSubmit}
            className={form.formWrapper}
            data-testid="documents_admin_form_start">
            <MultiSelect
                id="documentTypes"
                key={keys.documentTypes}
                label={'Document Types'}
                placeholder="Document types"
                disabled={!hasRole(acceptedRoles, roles)}
                mandatory={true}
                menuItems={documentTypes || []}
                preSelectedIds={preSelectedDocumentTypeIds}
                preSelects={preSelectedDocumentTypes}
                error={errors.documentTypes}
                onChange={(chosenIds) => onDocumentTypesChange(chosenIds)}
            />
            {errors?.appointmentTypes?.length > 0 && (
                <div className={`${form.textInputErrors} ${app.fadein}`}>
                    {errors.appointmentTypes.map((el) => (
                        <div key={el.id}>
                            <IconError text={el} />
                        </div>
                    ))}
                </div>
            )}

            <Button
                id="documentsAdmin"
                disabled={!hasRole(acceptedRoles, roles) || isDisabled}
                content="UPDATE DOCUMENTS"
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

DocumentsAdmin.propTypes = {
    contractId: PropTypes.string
};

export default DocumentsAdmin;
